import React, { useSyncExternalStore } from "react";
import { useSelector } from "react-redux";
import {
  Toast,
  ToastContainer as XuiToastContainer,
} from "@xfrontend/xui-react-plus";
import { RootState } from "#/types/RootState";

// --- Toast Store (imperative → declarative bridge) ---

type ToastType = "info" | "success" | "warning" | "error" | "default";

interface ToastItem {
  id: number;
  message: string;
  type: ToastType;
  show: boolean;
  delay: number;
  position: string;
  createdAt: string;
}

let nextId = 0;
let toasts: ToastItem[] = [];
const listeners = new Set<() => void>();
const toastTimers = new Map<number, ReturnType<typeof setTimeout>>();

function emitChange() {
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot() {
  return toasts;
}

function addToast(
  type: ToastType,
  message: string,
  options: { delay?: number; position?: string } = {},
) {
  const id = nextId;
  nextId += 1;
  const item: ToastItem = {
    id,
    message,
    type,
    show: true,
    delay: options.delay ?? 5000,
    position: options.position ?? "bottom-end",
    createdAt: new Date().toLocaleString("sv-SE").replace("T", " "),
  };
  toasts = [...toasts, item];
  emitChange();

  const timer = setTimeout(() => {
    removeToast(id);
  }, item.delay);
  toastTimers.set(id, timer);
}

function removeToast(id: number) {
  const timer = toastTimers.get(id);
  if (timer) {
    clearTimeout(timer);
    toastTimers.delete(id);
  }
  toasts = toasts.filter((t) => t.id !== id);
  emitChange();
}

// --- ToastContainer Component ---

const headerTypeMap: Record<string, "info" | "success" | "warning"> = {
  info: "info",
  success: "success",
  warning: "warning",
  error: "warning",
  default: "info",
};

const progressBarColorMap: Record<ToastType, string> = {
  default: "var(--x-gray-200, #f6f6f7)",
  info: "var(--x-gray-200, #f6f6f7)",
  success: "var(--x-cyan-light, #72f4ff)",
  warning: "var(--x-red, #ff595a)",
  error: "var(--x-purple, #8b7cff)",
};

const headerLabelMap: Record<ToastType, string> = {
  default: "Default",
  info: "Info",
  success: "Success",
  warning: "Warning",
  error: "Error",
};

function Container(): JSX.Element {
  const items = useSyncExternalStore(subscribe, getSnapshot);
  const { headerHeight } = useSelector((state: RootState) => state.infra);
  const frameInset = 12;

  const bottomEnd = items.filter((t) => t.position === "bottom-end");
  const topCenter = items.filter((t) => t.position === "top-center");
  const middleCenter = items.filter((t) => t.position === "middle-center");

  return (
    <div
      style={{
        position: "fixed",
        top: (headerHeight || 0) + frameInset,
        right: frameInset,
        bottom: frameInset,
        left: frameInset,
        zIndex: 1080,
        pointerEvents: "none",
      }}
    >
      {bottomEnd.length > 0 && (
        <XuiToastContainer position="bottom-end">
          {bottomEnd.map((item) => (
            <Toast
              key={item.id}
              show={item.show}
              bg="dark"
              style={{ pointerEvents: "auto" }}
              onClose={() => removeToast(item.id)}
            >
              <Toast.Header type={headerTypeMap[item.type]}>
                {headerLabelMap[item.type]}
                {(item.type === "error" || item.type === "warning") && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 12,
                      color: "var(--x-gray-400, #cfd0d3)",
                      paddingRight: 8,
                    }}
                  >
                    {item.createdAt}
                  </span>
                )}
              </Toast.Header>
              <Toast.Body>
                <div>{item.message}</div>
                {item.position !== "top-center" && (
                  <div
                    style={{
                      marginTop: 8,
                      height: 3,
                      width: "100%",
                      background: "rgba(255, 255, 255, 0.12)",
                      borderRadius: 999,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        height: "100%",
                        width: "100%",
                        background: progressBarColorMap[item.type],
                        transformOrigin: "left center",
                        animation: `toast-progress-${item.id} ${item.delay}ms linear forwards`,
                      }}
                    />
                    <style>
                      {`@keyframes toast-progress-${item.id} { from { transform: scaleX(1); } to { transform: scaleX(0); } }`}
                    </style>
                  </div>
                )}
              </Toast.Body>
            </Toast>
          ))}
        </XuiToastContainer>
      )}
      {topCenter.length > 0 && (
        <XuiToastContainer position="top-center">
          {topCenter.map((item) => (
            <Toast
              key={item.id}
              show={item.show}
              bg="dark"
              style={{ pointerEvents: "auto" }}
              onClose={() => removeToast(item.id)}
            >
              <Toast.Body>{item.message}</Toast.Body>
            </Toast>
          ))}
        </XuiToastContainer>
      )}
      {middleCenter.length > 0 && (
        <XuiToastContainer position="middle-center">
          {middleCenter.map((item) => (
            <Toast
              key={item.id}
              show={item.show}
              bg="dark"
              style={{ pointerEvents: "auto" }}
              onClose={() => removeToast(item.id)}
            >
              <Toast.Header type={headerTypeMap[item.type]}>
                {item.type === "error" ? "Error" : item.type}
                {(item.type === "error" || item.type === "warning") && (
                  <span style={{
                    marginLeft: "auto",
                    fontSize: 12,
                    color: "var(--x-gray-400, #cfd0d3)",
                    paddingRight: 8,
                  }}
                  >
                    {item.createdAt}
                  </span>
                )}
              </Toast.Header>
              <Toast.Body>{item.message}</Toast.Body>
            </Toast>
          ))}
        </XuiToastContainer>
      )}
    </div>
  );
}

// --- Imperative toast API ---

const instance = function toast(message: string): void {
  addToast("default", message);
};
instance.info = function info(message: string) {
  addToast("info", message);
};
instance.success = function success(message: string) {
  addToast("success", message);
};
instance.warn = function warn(message: string) {
  addToast("warning", message);
};
instance.error = function error(message: string) {
  addToast("error", message, { delay: 15000 });
};
instance.copied = function copied(message: string) {
  addToast("default", message, { delay: 1000, position: "top-center" });
};

export const ToastContainer = Container;
export const toast = instance;
